import React, { useEffect, useMemo, useState } from 'react';
import cx from 'classnames';
import dayjs from 'dayjs';

import {
  Bus,
  constants,
  getIso2FromIso3,
  OutcomeService,
  printMissingConfigError,
  SiteConfigManager,
  useBetslipState,
  useSettings,
} from '@atlas/core';

import eventShape from 'common/PropTypes/eventShape';

const BetBooster = ({ event, suspended }) => {
  const { betItems } = useBetslipState();
  const { lang, oddsFormat } = useSettings();
  const iso2Lang = getIso2FromIso3(lang);

  const {
    id: eventId,
    marketTypes,
    participants: {
      away: { fullName: awayName, fullNameEng: awayNameEng } = {},
      home: { fullName: homeName, fullNameEng: homeNameEng } = {},
    } = {},
    sportName,
    startTime,
  } = event;

  const zeroes = SiteConfigManager.getConfig('config.config.odds_zeroes', false);

  const [insightsCount, setInsightsCount] = useState();

  useEffect(() => {
    const onInsightsCountChanged = ({ awayName, homeName, insightsCount }) => {
      if (homeName === homeNameEng && awayName === awayNameEng) {
        setInsightsCount(insightsCount);
      }
    };

    Bus.on('betBooster:insidesChanged', onInsightsCountChanged);

    return () => {
      Bus.off('betBooster:insidesChanged', onInsightsCountChanged);
    };
  }, []);

  const activeSelections = useMemo(() => {
    const relatedBetItems = betItems.filter(betItem => betItem?.eventId === eventId);
    const outcomeIds = relatedBetItems.map(betItem => betItem?.outcomeId);

    return outcomeIds.join(', ');
  }, [betItems]);

  const betMarkets = [];

  marketTypes.map(marketType => {
    const { betGroups, id: marketTypeId, markets, name } = marketType;
    markets.map(market => {
      const { marketId, outcomes, specifiers, status } = market;
      if (status !== constants.STATUS.ACTIVE) {
        return;
      }

      const bets = [];
      outcomes.map(outcome => {
        const { id, name, odds, shortName, status } = outcome;
        if (status !== constants.STATUS.ACTIVE) {
          return;
        }

        bets.push({
          displayName: name || shortName,
          id,
          name: shortName || name,
          odds,
          oddsToDisplay: OutcomeService.formatOdds(odds, { oddsFormat, zeroes }),
        });
      });

      const compactBets = bets?.filter(Boolean);

      if (compactBets.length !== 0) {
        betMarkets.push({
          betGroups,
          bets: compactBets,
          eventId,
          id: marketTypeId,
          line: specifiers,
          marketId,
          marketTypeId,
          name,
        });
      }
    });
  });

  const date = dayjs(startTime).utc().format('DD/MM/YYYY HH:mm');
  const betBoosterApiKey = SiteConfigManager.getConfig('config.config.betBooster.apiKey');

  const betBoosterClass = cx('bet-booster-container', { multi: insightsCount > 1, suspended });

  if (!betBoosterApiKey) {
    printMissingConfigError('config.config.betBooster.apiKey');
  }

  return (
    <div className={betBoosterClass}>
      <betbooster-widget
        activeselections={activeSelections}
        apiKey={betBoosterApiKey}
        away={awayName}
        betmarkets={JSON.stringify(betMarkets)}
        date={date}
        home={homeName}
        lang={iso2Lang}
        originaway={awayNameEng}
        originhome={homeNameEng}
        originleague={iso2Lang}
        sport={sportName}
        title="Insights"
      />
    </div>
  );
};

BetBooster.propTypes = { event: eventShape.isRequired };

const BetBoosterWrapper = ({ event, suspended }) => {
  const isBetBoosterActive = SiteConfigManager.getConfig('config.config.betBooster.isActive');

  if (!isBetBoosterActive || event.sportService === constants.SPORT_SERVICE.ARCHIVED) {
    return null;
  }

  return <BetBooster event={event} suspended={suspended} />;
};

BetBoosterWrapper.propTypes = { event: eventShape.isRequired };

export default BetBoosterWrapper;
